// Subscription tier types for Pagoda Labs Booking Platform

export type SubscriptionTierType =
  | "independent"
  | "business_essentials"
  | "business_growth"
  | "medspa_essentials";

// Business verticals that subscription tiers can be gated to. Tiers without
// a `requiredBusinessVertical` are offered to every salon; tiers with one
// (e.g. MedSpa Essentials) are only offered when the salon matches.
export type SubscriptionBusinessVertical =
  | "hair"
  | "nail"
  | "barber"
  | "aesthetician"
  | "med_spa";

export interface SubscriptionFeature {
  name: string;
  included: boolean;
  note?: string;
}

export interface SubscriptionTier {
  id: SubscriptionTierType;
  name: string;
  price: number;
  priceLabel: string;
  target: string;
  employeeLimit: string;
  badge?: string;
  description: string;
  features: SubscriptionFeature[];
  highlighted?: boolean;
  // When set, this tier is only offered to salons matching this vertical.
  // Tiers without this field are offered to all salons except those whose
  // vertical maps to a different vertical-restricted tier.
  requiredBusinessVertical?: SubscriptionBusinessVertical;
  // Tiers with variable per-salon pricing (e.g. MedSpa Essentials) skip the
  // hardcoded `price` and fetch live price info from the backend.
  fetchLivePrice?: boolean;
}

export interface GetCheckoutUrlRequest {
  email: string;
  salonId: string;
  salonCreatedDt: string;
  subscriptionType: SubscriptionTierType;
  successUrl?: string;
  cancelUrl?: string;
}

export interface GetCheckoutUrlResponse {
  url: string;
}

export interface SubscriptionTierPricing {
  tier: SubscriptionTierType;
  price_id: string;
  unit_amount: number | null;
  currency: string | null;
  interval: string | null;
  interval_count: number | null;
}

// Subscription tier configurations
export const SUBSCRIPTION_TIERS: SubscriptionTier[] = [
  {
    id: "independent",
    name: "Independent",
    price: 99,
    priceLabel: "$99/mo",
    target: "Independents",
    employeeLimit: "1 employee",
    description:
      "For independents who want a professional presence but don't need full salon operations.",
    features: [
      { name: "Templated website", included: true },
      { name: "Online Booking", included: true },
      { name: "Calendar", included: true },
      { name: "CRM", included: true },
      { name: "Inventory", included: true },
      { name: "Deposits", included: true },
      { name: "Reminder texts to reduce no-shows", included: true },
      { name: "Reports", included: true },
      { name: "Marketing", included: true, note: "Credits system" },
    ],
  },
  {
    id: "business_essentials",
    name: "Business Essentials",
    price: 159,
    priceLabel: "$159/mo per location",
    target: "Small–mid salons + Hybrid Salons",
    employeeLimit: "Up to 5 employees",
    badge: "Most Popular",
    highlighted: true,
    description: "Everything you need to run a growing salon business.",
    features: [
      { name: "Everything from Independent tier", included: true },
      { name: "Basic AI features", included: true },
      { name: "Marketing", included: true },
      { name: "Booth renter support", included: true },
      {
        name: "Pagoda visibility add-on",
        included: true,
        note: "10% off monthly",
      },
    ],
  },
  {
    id: "business_growth",
    name: "Business Growth",
    price: 239,
    priceLabel: "$239/mo per location",
    target: "Growing & established salons",
    employeeLimit: "Up to 15 employees",
    description: "Full-featured solution for larger operations.",
    features: [
      { name: "Everything from Independent tier", included: true },
      { name: "Basic AI features", included: true },
      { name: "Marketing", included: true },
      { name: "Booth renter support", included: true },
      { name: "Free text & email credits", included: true },
      {
        name: "Pagoda visibility add-on",
        included: true,
        note: "15% off monthly",
      },
    ],
  },
  {
    id: "medspa_essentials",
    name: "MedSpa Essentials",
    // Pricing is per-salon — the live Stripe price (fetched via
    // `getSubscriptionPricing`) is the source of truth and will replace this
    // value once loaded. `price` is the fallback rendered if the live fetch
    // fails; keep it close to the typical MedSpa rate so a Stripe outage
    // doesn't surface "$0/mo".
    price: 299,
    priceLabel: "$299/mo",
    target: "Med Spas",
    employeeLimit: "Up to 5 employees",
    requiredBusinessVertical: "med_spa",
    fetchLivePrice: true,
    badge: "MedSpa",
    highlighted: true,
    description:
      "Tailored for Med Spas, with HIPAA-aware workflows and pricing set by our team.",
    features: [
      { name: "Everything from Business Essentials tier", included: true },
      { name: "MedSpa-specific intake forms & waivers", included: true },
      { name: "Clinical client documents (PHI)", included: true },
      { name: "Basic AI features", included: true },
      { name: "Marketing", included: true },
    ],
  },
];
